import { useState, useEffect } from 'react';
import { getPortalGroups } from '../../../services/api/apiClient';

interface ApiGroup {
  PortalGroupUid: string;
  name: string;
  subGroups: Array<{
    PortalGroupUid: string;
    name: string;
  }>;
}

export interface GroupOption {
  id: string;
  name: string;
  subGroups: SubGroupOption[];
}

export interface SubGroupOption {
  id: string;
  name: string;
}

export const usePortalGroups = (
  portalId: string | undefined,
  enabled: boolean = true,
) => {
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGroups = async () => {
      if (!enabled || !portalId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await getPortalGroups(portalId, { short: true } as any) as unknown as ApiGroup[];
        
        if (!Array.isArray(response)) {
          throw new Error('Invalid response format: expected an array of groups');
        }
        
        // Create a map of all groups by their ID
        const groupsMap = new Map<string, GroupOption>();
        
        // First pass: create all groups and subgroups
        response.forEach((group: ApiGroup) => {
          // Add the main group if it doesn't exist
          if (!groupsMap.has(group.PortalGroupUid)) {
            groupsMap.set(group.PortalGroupUid, {
              id: group.PortalGroupUid,
              name: group.name,
              subGroups: []
            });
          }
          
          // Add all subgroups
          if (Array.isArray(group.subGroups)) {
            group.subGroups.forEach((subGroup) => {
              if (!groupsMap.has(subGroup.PortalGroupUid)) {
                groupsMap.set(subGroup.PortalGroupUid, {
                  id: subGroup.PortalGroupUid,
                  name: subGroup.name,
                  subGroups: []
                });
              }
            });
          }
        });
        
        // Second pass: build the hierarchy
        const rootGroups: GroupOption[] = [];
        const processedGroups = new Set<string>();
        
        // Process each group in the response
        response.forEach((group: ApiGroup) => {
          const groupObj = groupsMap.get(group.PortalGroupUid);
          if (!groupObj) return;
          
          // Skip if we've already processed this group
          if (processedGroups.has(groupObj.id)) return;
          
          // Add subgroups to this group
          if (Array.isArray(group.subGroups)) {
            group.subGroups.forEach((subGroup) => {
              const subGroupObj = groupsMap.get(subGroup.PortalGroupUid);
              if (subGroupObj && !groupObj.subGroups.some(sg => sg.id === subGroupObj.id)) {
                groupObj.subGroups.push({
                  id: subGroupObj.id,
                  name: subGroupObj.name
                });
                processedGroups.add(subGroupObj.id);
              }
            });
          }
          
          // If this group is not a subgroup of any other group, add it to root groups
          const isSubgroup = response.some(g => 
            g.subGroups?.some(sg => sg.PortalGroupUid === group.PortalGroupUid)
          );
          
          if (!isSubgroup) {
            rootGroups.push(groupObj);
            processedGroups.add(groupObj.id);
          }
        });
        
        setGroups(rootGroups);
      } catch (err: any) {
        console.error('Failed to fetch groups:', {
          error: err,
          message: err.message,
          response: err.response?.data,
        });
        setError(`Failed to load groups: ${err.message || 'Unknown error'}`);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [portalId, enabled]);

  return { groups, loading, error };
};
